import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { Reveal, VisuallyHidden } from '@/components/atoms';
import { SectionHeading } from '@/components/molecules';
import { Section } from '@/components/organisms';
import { type ClientSector } from '@/content/clients';

import { clientCount, rosterLede, ROSTER_TITLE_ID } from '../content';
import { type ClientRosterState } from '../hooks/useClientRoster';
import { ClientCard, ClientSectorChips, SectorMatchCta } from '../molecules';

/**
 * The narrowest a card may be, and why the grid is `auto-fill` rather than a
 * breakpoint map.
 *
 * At 150px the grid lays out two columns inside a 360px phone's content width
 * and keeps adding them as the viewport grows — six at `lg` — with no
 * breakpoint for anyone to get wrong. A card is a mark and a name, so it has
 * no layout of its own that a particular column count would protect; it only
 * needs to be wide enough that a two-word name does not set one word per line.
 */
const MIN_CARD = 150;

type Props = {
  /** Lifted to `ClientsPage`, because two bands now read it. */
  roster: ClientRosterState;
  /** Carries the filtered sector into the enquiry form below the band. */
  onRequestQuote: (sector: ClientSector | null) => void;
};

/**
 * The full client roster, grouped by sector and narrowable to one.
 *
 * This band is why /clients exists. The home page's marquee does recognition —
 * eighteen marks a Ugandan buyer knows, read in a glance — and can do nothing
 * for the visitor this page is for: someone buying on behalf of an
 * organisation, who wants to know whether anyone *like them* has bought here
 * before. That is a scanning question, and scanning is what a moving row of
 * tiles is worst at. So the roster is a static grid, grouped by sector,
 * with a filter.
 *
 * ## Grouped, not sorted
 *
 * Fifteen headings with their counts answer "do you supply anyone like me"
 * without the reader taking an action; an A–Z of the whole roster makes them read all
 * of it. The headings are real `h3`s under the band's `h2`, so the page has an
 * outline a screen reader can jump through, and each carries its sector's own
 * id — `#government`, `#banking-insurance` — so a link can point at one.
 *
 * ## What the filter is and is not
 *
 * It narrows this list in place. It is not a tab set, not a route and not a
 * query parameter (see `useClientRoster` for why), and it starts off, so the
 * prerendered HTML carries every name rather than one sector's worth.
 * The count under the chips is a live region because the change it reports —
 * fifteen groups becoming one, somewhere below the chip that was tapped — is
 * exactly the kind a sighted reader sees and a screen reader otherwise misses.
 *
 * ## The filter arrives as a prop now
 *
 * It used to be this band's own state. `SectorMatchCta` at the foot of the
 * roster and the enquiry form below the band both read it — the ask is written
 * from whatever sector the reader filtered to, and the form's sector is
 * prefilled from it — so the hook moved up to `ClientsPage` and is handed
 * down. The band still decides nothing about the filter; it only draws it.
 *
 * ## Why the band ends in an offer
 *
 * A reader who has just filtered to their own sector has done the page's work
 * for it: they have said who they are. Closing the roster with the count they
 * are looking at and an ask in the same card is the one place on this page
 * where proof and offer are the same eyeful — see `SectorMatchCta`.
 */
export function ClientRosterBand({ roster, onRequestQuote }: Props) {
  const { sector, select, clear, groups, shown } = roster;

  return (
    <Section tone="muted" pattern="columns" labelledBy={ROSTER_TITLE_ID}>
      <SectionHeading
        id={ROSTER_TITLE_ID}
        eyebrow="The roster"
        title="Every organisation we have supplied"
        lede={rosterLede}
        align="center"
      />

      <Stack spacing={6} sx={{ mt: { xs: 8, md: 12 } }}>
        <ClientSectorChips sector={sector} onSelect={select} onClear={clear} />

        <Typography
          aria-live="polite"
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: 'center' }}
        >
          {sector
            ? `Showing ${shown} of ${clientCount} clients in ${sector}.`
            : `Showing all ${clientCount} clients.`}
        </Typography>

        <Stack spacing={{ xs: 8, md: 12 }}>
          {groups.map((group) => (
            <Box key={group.sector} component="section" aria-labelledby={`sector-${group.slug}`}>
              <Stack
                direction="row"
                sx={{ alignItems: 'baseline', gap: 2, mb: 4, flexWrap: 'wrap' }}
              >
                <Typography
                  id={`sector-${group.slug}`}
                  component="h3"
                  variant="h5"
                  // The anchor target, so `/clients#banking-insurance` lands
                  // below the sticky header rather than under it.
                  sx={{ scrollMarginTop: (theme) => theme.spacing(24) }}
                >
                  {group.sector}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {group.clients.length}{' '}
                  {group.clients.length === 1 ? 'organisation' : 'organisations'}
                </Typography>
              </Stack>

              <Reveal>
                <Box
                  component="ul"
                  sx={{
                    m: 0,
                    p: 0,
                    display: 'grid',
                    gridTemplateColumns: `repeat(auto-fill, minmax(${MIN_CARD}px, 1fr))`,
                    gap: { xs: 3, md: 4 },
                  }}
                >
                  {group.clients.map((client) => (
                    <ClientCard key={client.slug} client={client} />
                  ))}
                </Box>
              </Reveal>
            </Box>
          ))}
        </Stack>

        {/* The ask, at the end of the proof rather than three bands below it.
            It stays inside this `Stack` so it inherits the roster's own rhythm
            and reads as the list's closing line, not as a new band. */}
        <SectorMatchCta sector={sector} shown={shown} onRequest={() => onRequestQuote(sector)} />
      </Stack>

      {/* The grid above is readable text, so this adds the one thing it cannot
          say: that the list is the whole list. A reader who has filtered to one
          sector has been told the total by the live region. */}
      <VisuallyHidden>
        This is the complete list of organisations Kabex Motors has supplied vehicles to.
      </VisuallyHidden>
    </Section>
  );
}
